import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, LoaderIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
export const InsightsSection = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [buttonHovered, setButtonHovered] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setSubmitStatus({
        success: false,
        message: 'Please enter your email address.'
      });
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const formData = new FormData();
      formData.append('email_address', email);
      formData.append('tags', 'subscribe'); // Add the "subscribe" tag
      const response = await fetch('https://app.kit.com/forms/8408048/subscriptions', {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer blo_gIPpz-EVmBjL0RaPcA'
        }
      });
      if (response.ok) {
        setSubmitStatus({
          success: true,
          message: "Thank you for subscribing! You will receive a confirmation email shortly. If you haven't received it within a few minutes, check your spam folder and be sure to move our emails to your primary folder so you receive them!"
        });
        setEmail('');
      } else {
        const errorData = await response.json().catch(() => null);
        if (errorData && errorData.error === 'Member Exists') {
          setSubmitStatus({
            success: true,
            message: "You're already subscribed! Thank you for your continued support."
          });
        } else {
          throw new Error('Submission failed');
        }
      }
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: 'There was an error submitting your subscription. Please try again.'
      });
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section className="py-24 border-t border-gray-100 relative" id="insights" data-id="element-345">
      <div className="max-w-4xl mx-auto mb-12" data-id="element-346">
        <h2 className="font-serif text-4xl font-bold text-center mb-6" data-id="element-347">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600" data-id="element-348">
            Insights
          </span>
        </h2>
        <p className="text-lg text-gray-700 text-center mb-12" data-id="element-349">
          Stay ahead with actionable AI insights and strategies for creators.
        </p>
      </div>
      {/* Newsletter Section */}
      <div className="max-w-4xl mx-auto" data-id="element-350">
        <div className="bg-white p-10 rounded-2xl shadow-md border border-gray-200" data-id="element-351">
          <div className="text-center mb-10" data-id="element-352">
            <h3 className="font-serif text-3xl font-bold mb-4" data-id="element-353">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600" data-id="element-354">
                AI Insights Newsletter & Content
              </span>
            </h3>
            <p className="text-lg text-gray-700" data-id="element-355">
              Get strategies, trend analysis, and case studies delivered weekly
              so you can make smarter creator decisions.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8" data-id="element-356">
            <div className="md:col-span-3 space-y-6" data-id="element-357">
              <div className="flex items-start" data-id="element-358">
                <div className="mr-3 bg-green-100 p-1 rounded-full" data-id="element-359">
                  <CheckIcon className="w-5 h-5 text-green-600" data-id="element-360" />
                </div>
                <div data-id="element-361">
                  <h4 className="font-medium text-lg" data-id="element-362">AI Trends & Analysis</h4>
                  <p className="text-gray-600" data-id="element-363">
                    Stay updated on the latest AI developments for creators
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-364">
                <div className="mr-3 bg-green-100 p-1 rounded-full" data-id="element-365">
                  <CheckIcon className="w-5 h-5 text-green-600" data-id="element-366" />
                </div>
                <div data-id="element-367">
                  <h4 className="font-medium text-lg" data-id="element-368">Growth Strategies</h4>
                  <p className="text-gray-600" data-id="element-369">
                    Practical tactics to grow your audience and income
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-370">
                <div className="mr-3 bg-green-100 p-1 rounded-full" data-id="element-371">
                  <CheckIcon className="w-5 h-5 text-green-600" data-id="element-372" />
                </div>
                <div data-id="element-373">
                  <h4 className="font-medium text-lg" data-id="element-374">
                    Tool Reviews & Tutorials
                  </h4>
                  <p className="text-gray-600" data-id="element-375">
                    Learn how to leverage the best AI tools for creators
                  </p>
                </div>
              </div>
            </div>
            <div className="md:col-span-2 bg-blue-50 p-6 rounded-xl" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-376">
              <form className="space-y-4" onSubmit={handleSubmit} data-id="element-377">
                <h4 className="font-medium text-lg" data-id="element-378">Join the newsletter</h4>
                <div data-id="element-379">
                  <input type="email" name="email_address" placeholder="Your email address" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" required data-id="element-380" />
                </div>
                {submitStatus && <div className={`p-3 rounded-lg ${submitStatus.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-381">
                    <div className="flex items-start" data-id="element-382">
                      {submitStatus.success ? <CheckCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-green-600" data-id="element-383" /> : <AlertCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5 text-red-600" data-id="element-384" />}
                      <p className="text-sm" data-id="element-385">{submitStatus.message}</p>
                    </div>
                  </div>}
                <button type="submit" className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg transition-all hover:shadow-lg hover:bg-blue-700 flex items-center justify-center" disabled={isSubmitting} data-id="element-386">
                  {isSubmitting ? <>
                      <LoaderIcon className="w-4 h-4 mr-2 animate-spin" data-id="element-387" />
                      <span data-id="element-388">Subscribing...</span>
                    </> : <>
                      <span data-id="element-389">Subscribe Now</span>
                      <ArrowRightIcon className={`w-4 h-4 ml-2 transition-transform duration-300 ${isHovered ? 'translate-x-1' : ''}`} data-id="element-390" />
                    </>}
                </button>
              </form>
            </div>
          </div>
          <div className="mt-10 text-center" data-id="element-391">
            <Link to="/insights" className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg transition-all hover:shadow-lg hover:bg-blue-700" onMouseEnter={() => setButtonHovered(true)} onMouseLeave={() => setButtonHovered(false)} data-id="element-392">
              <span data-id="element-393">Discover Insights</span>
              <ArrowRightIcon className={`w-4 h-4 ml-2 transition-transform duration-300 ${buttonHovered ? 'translate-x-1' : ''}`} data-id="element-394" />
            </Link>
          </div>
        </div>
      </div>
    </section>;
};